import { useMemo } from 'react';
import { GridCellProps } from 'react-virtualized';
import * as MultiGridStyles from '~/components/MultiGrid/styles';
import { EMAIL_ALL_FIELDS, EMAIL_FIELDS_ORDER } from './constants';
import { CELL_SETTINGS } from '~/components/MultiGrid/constants';
import HeaderContent from './HeaderContent';
import CellContent from './CellContent';
import { useEmailListContext } from './hooks/useEmailListContext';

interface IGridCellProps {
  gridProps: GridCellProps;
  cellProps: {
    multiGridSettings: Readonly<{
      fixedRowCount: number;
      fixedColumnCount: number;
    }>;
  };
}

const GridCell = ({ gridProps: { columnIndex, rowIndex, style }, cellProps }: IGridCellProps) => {
  const { listIds, listData } = useEmailListContext();

  const thisEmail = listData[listIds[rowIndex - 1]];

  const field = EMAIL_ALL_FIELDS[EMAIL_FIELDS_ORDER[columnIndex]];
  const { multiGridSettings } = cellProps;

  const rowIndexType = useMemo(() => {
    if (rowIndex === 0) {
      return 'first';
    }
    return rowIndex === listIds.length ? 'last' : null;
  }, [rowIndex, listIds.length]);

  const columnIndexType = useMemo(() => {
    if (columnIndex === 0) {
      return 'first';
    }
    return columnIndex === EMAIL_FIELDS_ORDER.length - 1 ? 'last' : null;
  }, [columnIndex]);

  const isLastStaticColumn = useMemo(
    () => columnIndex === multiGridSettings.fixedColumnCount - 1,
    [columnIndex, multiGridSettings.fixedColumnCount],
  );

  if (rowIndex === 0) {
    return (
      <MultiGridStyles.HeaderCell
        columnIndexType={columnIndexType}
        style={style}
        {...{ [CELL_SETTINGS.lastColumnDataAttribute]: isLastStaticColumn }}
      >
        <HeaderContent fieldData={field} />
        {isLastStaticColumn && <MultiGridStyles.AbsoluteBorder />}
      </MultiGridStyles.HeaderCell>
    );
  }

  if (thisEmail) {
    return (
      <MultiGridStyles.BodyCell
        className="align-center"
        rowIndexType={rowIndexType}
        columnIndexType={columnIndexType}
        paddingLayer={field.isCellFullWidth ? 'first_child' : 'self'}
        style={style}
        {...{ [CELL_SETTINGS.lastColumnDataAttribute]: isLastStaticColumn }}
      >
        <CellContent fieldData={field} rowIndex={rowIndex} />
        {isLastStaticColumn && <MultiGridStyles.AbsoluteBorder />}
      </MultiGridStyles.BodyCell>
    );
  }

  return null;
};

export default GridCell;
